import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import QuoteForm from '../components/QuoteForm';
import { Compass, Building, Handshake, UploadCloud, Clock, ShieldCheck, Phone, Mail } from 'lucide-react';
import { Seo } from '../components/Seo';

const audience = [
  { icon: Compass, title: 'Architectes & maîtres d\'œuvre', desc: 'Un partenaire d\'exécution fiable pour vos projets clients, du chiffrage à la réalisation.' },
  { icon: Building, title: 'Syndics & gestionnaires', desc: 'Réponse aux consultations, ravalements et rénovations d\'immeubles avec un suivi rigoureux.' },
  { icon: Handshake, title: 'Agents & apporteurs d\'affaires', desc: 'Confiez-nous une affaire : nous étudions, chiffrons et vous tenons informé du suivi.' },
];

const perks = [
  { icon: UploadCloud, title: 'Dépôt de dossier simplifié', desc: 'Transmettez plans, CCTP, DPGF, photos et devis directement en ligne (jusqu\'à 10 fichiers).' },
  { icon: Clock, title: 'Réponse rapide', desc: 'Nous revenons vers vous rapidement après réception de votre dossier.' },
  { icon: ShieldCheck, title: 'Interlocuteur dédié', desc: 'Un suivi clair et professionnel, du chiffrage à la livraison du chantier.' },
];

const Partenaires = () => {
  return (
    <main className="pt-28" data-testid="page-partenaires">
      <Seo
        title="Pros & Partenaires : déposez votre dossier | VIREO"
        description="Architectes, syndics, maîtres d’œuvre et apporteurs d’affaires : transmettez votre dossier de consultation (plans, CCTP, DPGF). VIREO étudie et chiffre vos projets en Île-de-France."
        path="/partenaires-professionnels"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1503387837-b154d5074bd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Plans et dossiers de consultation"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full px-4 py-2 text-emerald-200 text-sm font-medium mb-6">
              <Handshake className="h-4 w-4" /> Pros & Partenaires
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Transmettez-nous une affaire en quelques clics
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed">
              Architectes, syndics, maîtres d'œuvre, professionnels de l'immobilier et apporteurs
              d'affaires : déposez votre dossier de consultation (plans, CCTP, DPGF, photos, devis).
              Notre équipe l'étudie et le chiffre.
            </p>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            {audience.map((a, i) => {
              const Icon = a.icon;
              return (
                <Card key={i} data-testid={`partner-audience-${i}`} className="border-slate-200 hover:border-emerald-500 hover:shadow-lg transition-all duration-300 bg-white group">
                  <CardContent className="p-7">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors duration-300">
                      <Icon className="h-6 w-6 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{a.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{a.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Perks + Form */}
      <section id="dossier" className="py-20 bg-slate-50 scroll-mt-28">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Perks */}
            <div className="space-y-6">
              {perks.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Card key={i} className="border-slate-200 bg-white">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-1">{p.title}</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-none">
                <CardContent className="pt-6 space-y-3">
                  <p className="text-white/90 text-sm">Vous préférez nous contacter directement ?</p>
                  <a href="tel:+33185097125" className="flex items-center gap-2 text-white font-semibold">
                    <Phone className="h-4 w-4" /> 01 85 09 71 25
                  </a>
                  <a href="mailto:devis@vireo-renovation.fr" className="flex items-center gap-2 text-white font-semibold break-all">
                    <Mail className="h-4 w-4" /> devis@vireo-renovation.fr
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200 shadow-xl bg-white">
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900">Déposer un dossier</CardTitle>
                  <p className="text-slate-600">
                    Renseignez votre affaire et joignez vos pièces (plans, CCTP, DPGF, photos, devis).
                  </p>
                </CardHeader>
                <CardContent>
                  <QuoteForm variant="partner" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Partenaires;
